import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { patientAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ArrowRight } from 'lucide-react';

export default function SelectPatientForReportPage() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [wounds, setWounds] = useState([]);
  const [selectedWound, setSelectedWound] = useState('');

  useEffect(() => {
    patientAPI.list({ status: 'aktiv' }).then(res => setPatients(res.data)).catch(console.error);
  }, []);

  useEffect(() => {
    if (selectedPatient) {
      patientAPI.get(selectedPatient).then(res => {
        setWounds(res.data.wounds || []);
        setSelectedWound('');
      }).catch(console.error);
    }
  }, [selectedPatient]);

  return (
    <div className="p-4 md:p-6 lg:p-8 max-w-2xl mx-auto space-y-4" data-testid="select-patient-report-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" onClick={() => navigate(-1)} className="p-2 rounded-xl" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <h1 className="text-xl md:text-2xl font-heading font-bold text-slate-900">Neuer Wundbericht</h1>
      </div>

      <Card className="border border-slate-200 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base">Patient & Wunde auswählen</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-700">Patient</label>
            <Select value={selectedPatient} onValueChange={setSelectedPatient}>
              <SelectTrigger className="h-12" data-testid="select-patient">
                <SelectValue placeholder="Patient auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {patients.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedPatient && wounds.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-700">Wunde</label>
              <Select value={selectedWound} onValueChange={setSelectedWound}>
                <SelectTrigger className="h-12" data-testid="select-wound">
                  <SelectValue placeholder="Wunde auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {wounds.map(w => (
                    <SelectItem key={w.id} value={w.id}>{w.wundart} - {w.lokalisation}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedPatient && wounds.length === 0 && (
            <div className="text-center py-4">
              <p className="text-sm text-slate-500 mb-3">Keine Wunden für diesen Patienten. Zuerst eine Wunde anlegen.</p>
              <Button
                onClick={() => navigate(`/patienten/${selectedPatient}/neue-wunde`)}
                className="bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
                data-testid="create-wound-first-btn"
              >
                Neue Wunde anlegen
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedWound && (
        <Button
          onClick={() => navigate(`/wunde/${selectedWound}/neuer-bericht`)}
          className="w-full h-12 bg-teal-700 hover:bg-teal-800 text-white rounded-xl"
          data-testid="proceed-report-btn"
        >
          Bericht erstellen
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      )}
    </div>
  );
}
